import type { MouseEvent, PointerEvent, ReactNode } from "react";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AgentCardInfo } from "./AgentCardInfo";
import type { AgentCardInfoKey } from "./agentCardInfoTexts";
import { cn } from "@/lib/utils";

type AgentConfigSectionProps = {
  value: string;
  title: string;
  emoji?: ReactNode;
  hint?: string;
  infoKey?: AgentCardInfoKey;
  infoClassName?: string;
  headerAction?: ReactNode;
  children: ReactNode;
  className?: string;
};

function stopTriggerToggle(event: MouseEvent | PointerEvent) {
  event.stopPropagation();
}

export function AgentConfigSection({
  value,
  title,
  emoji,
  hint,
  infoKey,
  infoClassName,
  headerAction,
  children,
  className,
}: AgentConfigSectionProps) {
  return (
    <AccordionItem
      value={value}
      className={cn(
        "agent-card !space-y-0 overflow-hidden rounded-xl border-0 p-0 shadow-sm",
        "data-[state=open]:shadow-md",
        className,
      )}
    >
      <AccordionTrigger
        className={cn(
          "group flex w-full min-h-[52px] items-center justify-start gap-2.5 sm:gap-3",
          "px-3 py-3 sm:px-5 sm:py-3.5",
          "text-left font-medium hover:no-underline",
          "[&>svg:last-child]:ml-1 sm:[&>svg:last-child]:ml-2",
          "[&>svg:last-child]:h-4 [&>svg:last-child]:w-4",
          "[&>svg:last-child]:shrink-0 [&>svg:last-child]:text-muted-foreground",
        )}
      >
        {emoji ? (
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/40 text-base leading-none sm:h-9 sm:w-9 sm:text-lg">
            {emoji}
          </span>
        ) : null}

        <div className="min-w-0 flex-1 pr-1">
          <span className="agent-section-title block text-sm font-semibold leading-tight sm:text-[0.9375rem]">
            {title}
          </span>
          {hint ? (
            <span className="mt-0.5 block truncate text-xs font-normal text-muted-foreground group-data-[state=open]:hidden">
              {hint}
            </span>
          ) : null}
        </div>

        {headerAction ? (
          <div
            className="flex shrink-0 items-center"
            onPointerDown={stopTriggerToggle}
            onClick={stopTriggerToggle}
          >
            {headerAction}
          </div>
        ) : null}

        {infoKey ? (
          <div
            className="flex shrink-0 items-center"
            onPointerDown={stopTriggerToggle}
            onClick={stopTriggerToggle}
          >
            <AgentCardInfo infoKey={infoKey} variant="inline" className={infoClassName} />
          </div>
        ) : null}
      </AccordionTrigger>

      <AccordionContent className="px-3 pb-3 sm:px-5 sm:pb-5">
        <div className="space-y-4 border-t border-agent-border/70 pt-4">{children}</div>
      </AccordionContent>
    </AccordionItem>
  );
}

/** @deprecated Use AgentConfigSection */
export const AgentBehaviorSection = AgentConfigSection;
